import { useState } from 'react';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, ArrowLeft, CheckCircle, LayoutDashboard, Settings, Layers } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const TOUR_STEPS = [
    {
        title: 'SKG Yönetim Paneline Hoş Geldiniz! 👋',
        description: 'Yeni nesil içerik yönetim sistemimize giriş yaptınız. Bu kısa tur ile panelin temel özelliklerini keşfedelim.',
        icon: <LayoutDashboard className="w-16 h-16 text-primary mb-4" />,
    },
    {
        title: 'Sol Menü ile Hızlı Erişim',
        description: 'Ekranın sol tarafındaki menüyü kullanarak Ürünler, Kategoriler, CMS Sayfaları ve Gelen Başvurulara anında ulaşabilirsiniz.',
        icon: <Layers className="w-16 h-16 text-primary mb-4" />,
    },
    {
        title: 'Gelişmiş Ayarlar ve Yetkiler',
        description: 'Üst menüden temanızı değiştirebilir, arama yapabilir ve yetkiniz dahilindeki site ayarlarına erişebilirsiniz.',
        icon: <Settings className="w-16 h-16 text-primary mb-4" />,
    },
    {
        title: 'Her Şey Hazır!',
        description: 'Artık sistemi kullanmaya hazırsınız. Website verilerini güvenle ve kolayca yönetmeye hemen başlayın.',
        icon: <CheckCircle className="w-16 h-16 text-green-500 mb-4" />,
    }
];

export function OnboardingTourDialog() {
    const { user, accessToken, setUser } = useAuthStore((s) => s.auth);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Sadece şifre değiştirme işlemi TAMAMLANMIŞSA ve tur TAMAMLANMAMIŞSA açılır
    const isPasswordChangeNeeded = user?.requiresPasswordChange === true;
    const isTourNeeded = user?.hasCompletedTour === false;

    const isOpen = !isPasswordChangeNeeded && isTourNeeded;

    const handleNext = () => {
        if (currentStep < TOUR_STEPS.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleComplete = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/auth/complete-tour`,
                {},
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            if (user) {
                setUser({
                    ...user,
                    hasCompletedTour: true
                });
            }
            toast.success(response.data.message || 'Turu başarıyla tamamladınız. İyi çalışmalar!');
        } catch (error: any) {
            toast.error('Tur tamamlanırken bir hata oluştu.');
        } finally {
            setIsLoading(false);
        }
    };

    const step = TOUR_STEPS[currentStep];

    return (
        <AlertDialog open={isOpen} onOpenChange={(open) => {
            // Dışarı tıklama ile kapanmayı engelle
            if (!open) return;
        }}>
            <AlertDialogContent className="sm:max-w-[500px] text-center p-8">
                <div className="flex flex-col items-center justify-center min-h-[250px]">
                    {step.icon}
                    <h2 className="text-2xl font-bold tracking-tight mb-3">{step.title}</h2>
                    <p className="text-muted-foreground text-md leading-relaxed">
                        {step.description}
                    </p>
                </div>

                {/* Nokta Göstergeleri (Dots Indicator) */}
                <div className="flex justify-center gap-2 my-6">
                    {TOUR_STEPS.map((_, index) => (
                        <div
                            key={index}
                            className={`h-2 rounded-full transition-all duration-300 ${index === currentStep ? 'w-8 bg-primary' : 'w-2 bg-muted'
                                }`}
                        />
                    ))}
                </div>

                <AlertDialogFooter className="flex sm:justify-between w-full">
                    <Button
                        variant="outline"
                        onClick={handlePrev}
                        disabled={currentStep === 0 || isLoading}
                        className={currentStep === 0 ? 'invisible' : ''}
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Geri
                    </Button>

                    {currentStep < TOUR_STEPS.length - 1 ? (
                        <Button onClick={handleNext} disabled={isLoading}>
                            İleri
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    ) : (
                        <Button onClick={handleComplete} disabled={isLoading} className="bg-primary text-primary-foreground">
                            {isLoading ? 'Kaydediliyor...' : 'Başla !'}
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
